import { useTranslation } from "react-i18next";

import type { StallView } from "../../services/generated/client.js";
import { cn } from "../../lib/utils.js";

/**
 * The party is waiting on somebody who can move it on — phase 21, **ADR 0030**.
 *
 * # Why this is a component and not two panels
 *
 * A stall is the same fact on every screen, and the two surfaces that draw it —
 * the phone's stage and the television's board — differ only in whether there
 * are buttons under it. Building it twice is how the phone and the display drift
 * into describing different games, which is the reason `Timeline` and
 * `SideBoard` are shared already.
 *
 * # It renders at ten feet
 *
 * `big` switches the type up rather than switching to a different component, so
 * the television inherits every later change to the wording for free. No hover
 * states, and the state is carried by words rather than by colour alone —
 * `.claude/rules/frontend.md` treats both as requirements on `/display`.
 *
 * # ⚠️ The count comes from the server
 *
 * `needed` and `present` are on the wire because **the bar moves**: a majority
 * is of the players present *now*, so somebody leaving mid-vote lowers it. A
 * client that counted the roster would show "1 of 3" for a question that only
 * needs one more answer — and not waiting for people who have gone is the entire
 * point of the phase, so getting that number from anywhere else would undo it on
 * screen while the engine got it right.
 */
export function StallNotice({
  stall,
  big = false,
  yourVote = null,
  onVote,
}: {
  stall: StallView;
  /** Ten-foot sizing, for `/display`. */
  big?: boolean | undefined;
  /** What you have already said, if anything. `null` is *not yet answered*. */
  yourVote?: boolean | null | undefined;
  /**
   * Omitted on a screen that only reports — the television, a spectator.
   *
   * ⚠️ Explicitly `| undefined` because `exactOptionalPropertyTypes` is on: the
   * caller passes `undefined` for a spectator rather than conditionally spreading
   * the prop, and `?` alone does not permit that.
   */
  onVote?: ((close: boolean) => void) | undefined;
}) {
  const { t } = useTranslation();
  const closing = stall.votes.filter((vote) => vote.close).length;
  const waiting = stall.votes.length - closing;

  return (
    <section
      data-testid="stall-notice"
      className={cn(
        "rounded-xl border-2 border-amber-400/60 bg-amber-400/10",
        big ? "p-8" : "p-4",
      )}
    >
      <p className={cn("font-semibold", big ? "text-4xl" : "text-base")}>
        {stall.voting ? t("stall.voteTitle") : t("stall.waitingTitle")}
      </p>
      <p className={cn("mt-2 text-white/70", big ? "text-2xl" : "text-sm")}>
        {stall.voting ? t("stall.voteLead") : t("stall.waitingLead")}
      </p>

      {stall.voting && (
        <>
          {/* Both tallies, always — a room that can only see the "close" count
              cannot tell a vote that is going nowhere from one that has not
              started. */}
          <p className={cn("mt-3 text-white/60", big ? "text-2xl" : "text-sm")}>
            {t("stall.tally", { closing, waiting, needed: stall.needed })}
          </p>
          {onVote && (
            <div className="mt-4 flex gap-3">
              <VoteButton
                label={t("stall.keepWaiting")}
                chosen={yourVote === false}
                onClick={() => onVote(false)}
              />
              <VoteButton
                label={t("stall.closeParty")}
                chosen={yourVote === true}
                onClick={() => onVote(true)}
              />
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * One answer.
 *
 * Both buttons stay live after you have answered, because an answer may be
 * changed while the window is open and the engine replaces rather than appends.
 * Disabling the chosen one would make a change look impossible when it is not.
 */
function VoteButton({
  label,
  chosen,
  onClick,
}: {
  label: string;
  chosen: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={chosen}
      className={cn(
        "min-h-12 flex-1 rounded-xl border-2 px-4 text-base font-semibold",
        chosen ? "border-white bg-white/15" : "border-white/25",
      )}
    >
      {label}
    </button>
  );
}
